/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { DeviceModel } from '../../../core/device';
import { BrandingUiInfoKeys } from '../../../core/entities';
import { PCState } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { KeyManager, ScreenTaskControllerTypes } from '../../../core/navigation';
import type { TGetState, TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { BrandingUiUtility, ListTypes } from '../../../core/utils';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../list/list.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showPCPinOverlayForPCSettingsAccess,
    updatePcState,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    showAccountScreen,
    showGuideScreen,
    showHelpScreen,
    showInfoScreen,
    showManageScreen,
    showPCScreen,
    showPreviousScreen,
    showUserPreferencesScreen,
} from '../../screens/screens.reducers';
import { setSelectedSettingsOption, setSettingsOptionsListAction } from './mainsettings.actions';
import {
    SET_MAIN_SETTINGS_OPTIONS_LIST,
    SET_MAIN_SETTINGS_SELECTED_OPTION,
} from './mainsettings.types';

interface ISelectedOption {
    title: string;
    preview: string;
}

interface IMainSettings {
    optionsList: Array<ISelectedOption>;
    selectedOption: ISelectedOption | undefined;
}

const initialState: IMainSettings = {
    optionsList: [],
    selectedOption: undefined,
};

const mainSettings = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IMainSettings>>{},
) => {
    switch (type) {
        case SET_MAIN_SETTINGS_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
                selectedOption: state.selectedOption ?? payload.optionsList[0],
            };
        case SET_MAIN_SETTINGS_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        default:
            return state;
    }
};

export const setSettingsOptionsList = () => (dispatch: Function) => {
    const displayName = new ServicesClientImpl().getBrandingUiInfoForKey(
        BrandingUiInfoKeys.DISPLAY_NAME,
    );
    const todoListSettingsItems = [
        {
            title: Locale.getString(LocaleKeys.SETTINGS_MANAGE),
            preview: Locale.getString(LocaleKeys.SETTINGS_MANAGE_PREVIEW_TEXT),
        },
    ];
    const settingsItemList: Array<{ title: string; preview: string }> = [
        ...todoListSettingsItems,
        {
            title: Locale.getString(LocaleKeys.SETTINGS_USER_PREFERENCES),
            preview: Locale.getString(LocaleKeys.SETTINGS_USER_PREFERENCES_PREVIEW),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT),
            preview: Locale.getString(LocaleKeys.SETTINGS_PC_PREVIEW_TEXT),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_ACCOUNT),
            preview: Locale.getString(LocaleKeys.SETTINGS_ACCOUNT_PREVIEW_TEXT),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_HELP),
            preview: Locale.getString(LocaleKeys.SETTINGS_HELP_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_INFO_TITLE),
            preview: Locale.getString(LocaleKeys.SETTINGS_INFO_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
        },
    ];
    dispatch(setSettingsOptionsListAction(settingsItemList));
};

/**
 * Below reducers are related to operations on key events.
 */

const getStateValues = (getState: TGetState) => {
    const state = getState();
    return {
        position: state.list[ListTypes.MAIN_SETTINGS].position,
        optionsList: state.mainSettings.optionsList,
        screenTaskController: state.screens.screenTaskController,
    };
};

export const onKeyBackMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS:
            KeyManager.getInstance().startKeyIgnoring();
            dispatch(showPreviousScreen());
            break;

        default:
    }
};

export const onKeyDownMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { position, optionsList, screenTaskController } = getStateValues(getState);

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS:
            if (position < optionsList.length - 1) {
                dispatch(setSelectedSettingsOption(optionsList[position + 1]));
                dispatch(onKeyDownList(ListTypes.MAIN_SETTINGS));
            }
            break;

        default:
    }
};

export const onKeyDigitMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getStateValues(getState);

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyDigitPcPinOverlay());
    }
};

export const onKeyLeftMainSettigs = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyLeftPcPinOverlay());
    }
};

export const onKeyRightMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyRightPcPinOverlay());
    }
};

export const onKeyUpMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { position, optionsList, screenTaskController } = getStateValues(getState);

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS:
            if (position > 0) {
                dispatch(setSelectedSettingsOption(optionsList[position - 1]));
                dispatch(onKeyUpList(ListTypes.MAIN_SETTINGS));
            }
            break;

        default:
    }
};

export const onKeyGuideMainSettings = () => (dispatch: Function) => {
    dispatch(showGuideScreen());
};

export const onKeyOkMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const {
        list: {
            [ListTypes.MAIN_SETTINGS]: { position },
        },
        mainSettings: { optionsList },
    } = getState();

    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyOkPcPinOverlay());
        return;
    }

    const service = new ServicesClientImpl();
    const selectedItem = optionsList[position].title;
    const whisperMessage = BrandingUiUtility.formatDescription(
        Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
        {
            DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                BrandingUiInfoKeys.DEVICE_NAME_SHORT,
            ),
            BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
        },
    );
    const pcState = service.getPCState();

    switch (selectedItem) {
        case Locale.getString(LocaleKeys.SETTINGS_MANAGE):
            dispatch(showManageScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_USER_PREFERENCES):
            dispatch(showUserPreferencesScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_PC_PARENTAL_CONTROLS_TEXT):
            if (pcState !== PCState.Off) {
                dispatch(showPCPinOverlayForPCSettingsAccess(unlockPCSettingsOnPinVerify));
            } else {
                dispatch(showPCScreen());
            }
            break;

        case Locale.getString(LocaleKeys.SETTINGS_ACCOUNT):
            dispatch(showAccountScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_HELP):
            dispatch(showHelpScreen());
            break;

        case Locale.getString(LocaleKeys.SETTINGS_INFO_TITLE):
            dispatch(showInfoScreen());
            break;

        default:
    }
};

export const onMouseClickMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getStateValues(getState);

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS:
            const { value } = getState().inputFactory;
            if (value.includes(ListTypes.MAIN_SETTINGS)) dispatch(onKeyOkMainSettings());
            break;
        default:
    }
};

export const onMouseOutMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getStateValues(getState);

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS:
            dispatch(onMouseOutList());
            break;

        default:
    }
};

export const onMouseOverMainSettings = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getStateValues(getState);

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MAIN_SETTING_TASKS:
            dispatch(onMouseOverList());
            const {
                list: {
                    [ListTypes.MAIN_SETTINGS]: { position },
                },
                mainSettings: { optionsList },
            } = getState();
            dispatch(setSelectedSettingsOption(optionsList[position]));
            break;

        default:
    }
};

const unlockPCSettingsOnPinVerify = () => (dispatch: Function) => {
    const services = new ServicesClientImpl();
    const currentPCState = services.getPCState();
    const newPCState = currentPCState === PCState.Locked ? PCState.Unlocked : currentPCState;
    dispatch(updatePcState(newPCState));
    dispatch(showPCScreen());
};

export default mainSettings;
